/* eslint-disable camelcase */
import { Box, Typography } from '@mui/material'
import { ChangeEvent } from 'react'
import { Trans } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { Port } from '../../types/models'
import { EmSimpleConfig, EmSimplePort, PortConfigEmSimple } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Text } from '../../components/atoms/Text'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { HelpTitle } from '../../components/atoms/HelpTitle'
import { renderNetMeterCountWarning } from './netMeterWarning'

/**
 * Controller for simple power meter.
 */
export class SimplePowerMeterPortControl extends PortControl<EmSimplePort, EmSimpleConfig> {

  port: EmSimplePort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as EmSimplePort
  }

  getDescription(): string { return this.app.t('Energy consumption measurement using led reader') }

  getTypeName(): string { return this.app.t('PortType-Simple Power Meter') }

  get group(): string { return this.app.t('PortGroup-Energy Metering') }

  getConfigDefaults(): PortConfigEmSimple {
    return {
      enabled: false,
      to_summer: false,
      joules_per_blink: 3600
    }
  }

  configTopLeft() : React.ReactNode | undefined {
    const joules = this.getConfig('joules_per_blink')
    const toSummer = this.getConfig('to_summer')

    const onChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = parseInt(e.target.value) || 0

      this.setConfig('joules_per_blink', newValue)
      this.setConfig('enabled', true)
    }

    return <Box>
      <HelpTitle title={this.app.t('Settings')} code="EM_SIMPLE"/>

      <Box sx={{ display: 'flex' }}>
          <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('to_summer', !toSummer)} checked={!!toSummer}/>
          <Typography component="span" sx={{ ml: 2 }}>
            <Trans>Part of building net metering?</Trans>
          </Typography>
      </Box>

      {renderNetMeterCountWarning(this)}

      <Text
        sx={{ width: '80%' }}
        margin="normal"
        type="number"
        label={this.app.t('Joules per Blink')}
        name="joules_per_blink"
        size="small"
        value={joules || ''}
        onChange={onChange}
      />
    </Box>
  }

  configTopRight() : React.ReactNode | undefined {
    const { energy, power } = this.port.values

    return <Box>
      <Trans>Measuring Data</Trans>
      <Box data-cy="box-Power" sx={{ mt: 2 }}><Trans>Power</Trans>: {power === undefined ? <>—</> : `${sprintf('%f', power)} W`}</Box>
      <Box data-cy="box-Energy"><Trans>Energy</Trans>: {energy === undefined ? <>—</> : `${sprintf('%.3f', energy / 3.6e6)} kWh`}</Box>
    </Box>
  }
}
